import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/toast';
import { ApiException } from '@/shared/api/client';
import { createObjectSchema, type CreateObjectValues } from '@/shared/schemas/object.schema';
import { useCreateObject, useUpdateObject } from './objects.api';
import { useCustomers } from '@/features/customers/customers.api';
import type { SiteObject } from '@/shared/api/types';

function clean<T extends Record<string, unknown>>(v: T): Record<string, unknown> {
  return Object.fromEntries(Object.entries(v).filter(([, val]) => val !== undefined && val !== ''));
}

export function ObjectFormDialog({
  open,
  onClose,
  object,
  presetCustomerId,
}: {
  open: boolean;
  onClose: () => void;
  object?: SiteObject | null;
  presetCustomerId?: string;
}) {
  const create = useCreateObject();
  const update = useUpdateObject();
  const push = useToast((s) => s.push);
  const { data: customers } = useCustomers('');
  const isEdit = !!object;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateObjectValues & { active?: boolean }>({
    // Zwei explizite Schemas statt Typ-Trickserei: beim Bearbeiten ist
    // customerId nicht im Formular und darf nicht validiert werden.
    resolver: zodResolver(isEdit ? createObjectSchema.omit({ customerId: true }) : createObjectSchema),
    values: {
      customerId: object?.customer?.id ?? presetCustomerId ?? '',
      name: object?.name ?? '',
      address: object?.address ?? '',
      accessNotes: object?.accessNotes ?? '',
      photoPolicy: object?.photoPolicy ?? 'ALLOWED',
      active: object?.active ?? true,
    },
  });

  const onSubmit = async (values: CreateObjectValues & { active?: boolean }) => {
    try {
      if (isEdit && object) {
        const { customerId: _c, ...rest } = values;
        void _c;
        await update.mutateAsync({ id: object.id, input: clean(rest) });
        push('Objekt aktualisiert.');
      } else {
        await create.mutateAsync(clean(values) as CreateObjectValues);
        push('Objekt angelegt.');
      }
      reset();
      onClose();
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Speichern fehlgeschlagen.', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? 'Objekt bearbeiten' : 'Neues Objekt'}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {isEdit ? (
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
            <span className="text-slate-500">Kunde: </span>
            <span className="font-medium text-slate-900">{object?.customer?.companyName}</span>
            <span className="ml-2 text-xs text-slate-400">(nicht änderbar)</span>
          </div>
        ) : (
          <Select label="Kunde *" {...register('customerId')} error={errors.customerId?.message}>
            <option value="">Bitte wählen…</option>
            {customers?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.companyName}
              </option>
            ))}
          </Select>
        )}

        <Input label="Objektname *" {...register('name')} error={errors.name?.message} />
        <Input label="Adresse" {...register('address')} error={errors.address?.message} />

        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Zugangshinweise</label>
          <textarea
            {...register('accessNotes')}
            rows={2}
            placeholder="Schlüssel, Codes, Ansprechpartner vor Ort"
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>

        <Select label="Fotoregelung" {...register('photoPolicy')} error={errors.photoPolicy?.message}>
          <option value="ALLOWED">Fotos erlaubt</option>
          <option value="RESTRICTED">Fotos nur nach Absprache</option>
          <option value="FORBIDDEN">Keine Fotos (z. B. Arztpraxis, Datenschutz)</option>
        </Select>

        {isEdit && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" {...register('active')} className="h-4 w-4 rounded" />
            Objekt aktiv
          </label>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
